/**
 * Pieces shared by the three signed-out screens: sign in, forgot password, and
 * set a new password.
 *
 * Extracted when the second and third screens arrived (2026-09-18) so the
 * monogram SVG and the brand-by-address rule exist once. Three copies of the
 * header is how one screen would end up showing Tonie's brand on the product
 * domain after the others were fixed.
 */

import { esc } from '@/lib/dom';
import type { SignInBrand } from '@/lib/host-branding';
import { brandFontStack, brandColor } from '@/lib/brand-type';

/** localStorage key for the last email that signed in successfully. */
export const LAST_EMAIL_KEY = 'tsd:last-email';

/** Last successful sign-in email, or '' — never throws (private windows). */
export function readLastEmail(): string {
  try { return localStorage.getItem(LAST_EMAIL_KEY) ?? ''; } catch { return ''; }
}

export function authCardHeader(brand: SignInBrand, tagline: string): string {
  return `
    <svg class="boot-monogram" viewBox="0 0 64 64" aria-hidden="true">
      <circle cx="32" cy="32" r="30" stroke="#B89A6A" stroke-width="1.5" fill="none"/>
      <text x="32" y="40" text-anchor="middle" font-family="Cormorant Garamond, serif" font-size="26" fill="#1A1714" letter-spacing="2">${esc(brand.monogram)}</text>
    </svg>
    <h1 style="${esc(`font-family:${brandFontStack(brand.fontKey)};color:${brandColor(brand.color)}`)}">${esc(brand.name)}</h1>
    <div class="tagline">${esc(tagline)}</div>`;
}

/**
 * Friendlier wording for the errors a person can actually act on.
 *
 * Supabase rate-limits reset emails per address and answers with a 429 whose
 * message ("For security purposes, you can only request this after N
 * seconds") is accurate but reads like an accusation.
 */
export function authErrorMessage(err: unknown, fallback: string): string {
  const e = err as { status?: number; code?: string; message?: string } | null;
  if (e?.status === 429 || e?.code === 'over_email_send_rate_limit') {
    return 'Too many requests for this address. Please wait a minute and try again.';
  }
  if (e?.code === 'same_password') {
    return 'That’s your current password. Choose a different one.';
  }
  if (e?.code === 'weak_password') return weakPasswordMessage(err);
  return e?.message?.trim() ? e.message : fallback;
}

/**
 * Supabase's own password rules (Authentication → Policies) arrive as
 * `weak_password`, with `reasons` saying which rule failed. Its message lists
 * the required characters literally — "abcdefghijklmnopqrstuvwxyz,
 * ABCDEFGHIJKLMNOPQRSTUVWXYZ, 0123456789, !@#$%…" — which reads as noise.
 * Found on the first real invitation, 2026-09-18.
 */
export function weakPasswordMessage(err: unknown): string {
  const e = err as { message?: string; reasons?: unknown; weak_password?: { reasons?: unknown } } | null;
  const raw = e?.reasons ?? e?.weak_password?.reasons;
  const reasons = Array.isArray(raw) ? raw.map(String) : [];
  const message = e?.message ?? '';

  if (reasons.includes('pwned') || /known to be weak|easy to guess|pwned/i.test(message)) {
    return 'That password has turned up in a known data breach, so it’s easy to guess. Choose a different one.';
  }

  const parts: string[] = [];
  const len = /at least (\d+) characters/i.exec(message);
  if (reasons.includes('length') || len) {
    parts.push(`at least ${len ? len[1] : 'more'} characters`);
  }
  if (reasons.includes('characters') || /one character of each/i.test(message)) {
    const need = requiredCharacterKinds(message);
    parts.push(need.length ? `at least one ${joinAnd(need)}` : 'a mix of letters, numbers and symbols');
  }
  return parts.length
    ? `Your password needs ${joinAnd(parts)}.`
    : 'That password isn’t strong enough. Try a longer one with a mix of letters, numbers and symbols.';
}

/** Which character kinds Supabase's "one character of each: …" list asks for. */
function requiredCharacterKinds(message: string): string[] {
  const list = message.split(/one character of each:/i)[1] ?? '';
  const kinds: string[] = [];
  if (/[a-z]{10,}/.test(list)) kinds.push('lowercase letter');
  if (/[A-Z]{10,}/.test(list)) kinds.push('capital letter');
  if (/0123456789/.test(list)) kinds.push('number');
  if (/[!@#$%^&*]/.test(list)) kinds.push('symbol (such as ! or #)');
  return kinds;
}

function joinAnd(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
